import React, { useCallback, useEffect, useRef, useState } from "react";
import axios from "axios";
import { FiSend } from "react-icons/fi";
import { sendEmailVerification } from "firebase/auth";
import styles from "./SupportWidget.module.css";
import { auth } from "../../Confg/firebaseConfig";
import { getApiUrl } from "../../Confg/appConfig";

// Aba "Nina" do chat de suporte (dono, 09/10/2026): o restaurante fala com a Nina, a IA do
// WhatsApp da Goby, daqui do painel. Ela sabe qual loja é pelo email do login (a loja da Goby
// com esse email), então já responde sobre os pedidos dela. O email precisa estar confirmado:
// sem isso, qualquer um criaria uma conta com o email de um restaurante.
//
// Backend: GET /api/nina-web/loja e POST /api/nina-web/mensagem (Auuii-Backend, ninaWebRoutes.js).
// A conversa não é gravada: fechou a aba do navegador, sumiu (a memória da Nina guarda as
// últimas 8 mensagens).

const PERSONA = "Nina";

function api(metodo, caminho, corpo) {
    const url = `${String(getApiUrl() || "").replace(/\/$/, "")}/api/nina-web/${caminho}`;
    return (metodo === "get" ? axios.get(url) : axios.post(url, corpo)).then((r) => r.data);
}
function motivo(err) {
    return err?.response?.data?.error || "Não deu pra falar com a Nina agora.";
}

const NinaChat = () => {
    const [estado, setEstado] = useState({ etapa: "carregando" }); // carregando | pronto | erro
    const [texto, setTexto] = useState("");
    const [mensagens, setMensagens] = useState([]);
    const [ocupado, setOcupado] = useState(false);
    const [linkEnviado, setLinkEnviado] = useState(false);
    const fimRef = useRef(null);

    const carregar = useCallback(async () => {
        setEstado({ etapa: "carregando" });
        try {
            const r = await api("get", "loja");
            setEstado({ etapa: "pronto", loja: r.loja });
        } catch (err) {
            setEstado({ etapa: "erro", code: err?.response?.data?.code || null, erro: motivo(err) });
        }
    }, []);

    useEffect(() => { carregar(); }, [carregar]);
    useEffect(() => { fimRef.current?.scrollIntoView({ block: "end" }); }, [mensagens, ocupado]);

    async function mandarLink() {
        try {
            await sendEmailVerification(auth.currentUser);
            setLinkEnviado(true);
        } catch (err) {
            setEstado((e) => ({ ...e, erro: "Não deu pra mandar o link agora. Tente de novo em alguns minutos." }));
        }
    }

    // Depois de clicar no link do email: o token precisa ser renovado pra dizer "confirmado".
    async function jaConfirmei() {
        try {
            await auth.currentUser?.reload();
            await auth.currentUser?.getIdToken(true);
        } catch { /* segue: o carregar mostra o motivo */ }
        carregar();
    }

    async function mandar(e) {
        e.preventDefault();
        const msg = texto.trim();
        if (!msg || ocupado) return;
        setTexto("");
        setMensagens((m) => [...m, { autor: "eu", texto: msg }]);
        setOcupado(true);
        try {
            const r = await api("post", "mensagem", { texto: msg });
            setMensagens((m) => [...m, { autor: "nina", texto: r.resposta || "(sem resposta)" }]);
        } catch (err) {
            setMensagens((m) => [...m, { autor: "erro", texto: motivo(err) }]);
        } finally {
            setOcupado(false);
        }
    }

    if (estado.etapa !== "pronto") {
        return (
            <div className={styles.messagesArea}>
                {estado.etapa === "carregando" ? (
                    <p className={styles.loading}>Procurando a sua loja…</p>
                ) : (
                    <div className={styles.ninaAviso}>
                        <p role="alert">{estado.erro}</p>
                        {estado.code === "email_nao_confirmado" && (
                            linkEnviado ? (
                                <>
                                    <p>Mandamos um link pro {auth.currentUser?.email}. Clique nele e volte aqui.</p>
                                    <button type="button" className="ap-btn ap-btn-primary" onClick={jaConfirmei}>Já confirmei</button>
                                </>
                            ) : (
                                <button type="button" className="ap-btn ap-btn-primary" onClick={mandarLink}>Mandar link de confirmação</button>
                            )
                        )}
                        {estado.code !== "email_nao_confirmado" && (
                            <button type="button" className="ap-btn" onClick={carregar}>Tentar de novo</button>
                        )}
                    </div>
                )}
            </div>
        );
    }

    return (
        <>
            <div className={styles.ninaQuem}>
                <span>Falando como: {estado.loja?.nome || "sua loja"}</span>
            </div>
            <div className={styles.messagesArea} aria-live="polite">
                {mensagens.length === 0 && <p className={styles.placeholder}>Mande um “oi” pra {PERSONA}. Ela vê os pedidos da sua loja na Goby.</p>}
                {mensagens.map((m, i) => (
                    <div key={i} className={`${styles.message} ${m.autor === "eu" ? styles.sent : styles.received}`}>
                        <div className={styles.senderName}>{m.autor === "eu" ? "Você" : m.autor === "erro" ? "Erro" : PERSONA}</div>
                        <p>{m.texto}</p>
                    </div>
                ))}
                {ocupado && <div className={styles.typingIndicator}>A {PERSONA} está digitando…</div>}
                <div ref={fimRef} />
            </div>
            <form onSubmit={mandar} className={styles.inputArea}>
                <input value={texto} onChange={(e) => setTexto(e.target.value)} placeholder="Digite sua mensagem"
                    className={`ap-field-input ${styles.input}`} aria-label="Mensagem pra Nina" />
                <button type="submit" className="ap-btn ap-btn-primary" disabled={ocupado || !texto.trim()}>
                    <FiSend size={16} aria-hidden="true" /> Enviar
                </button>
            </form>
        </>
    );
};

export default NinaChat;
